import {
  GPGS_NO_CONDITION,
  type GpgsAchievementDef,
  type GpgsLeaderboardDef,
} from "../data/gpgsData.js";

/** GPGS 부작용 1건 — sink가 GpgsPort로 실행할 명령. */
export type GpgsAction =
  | { readonly type: "unlock"; readonly achievementId: string }
  | { readonly type: "increment"; readonly achievementId: string; readonly steps: number }
  | { readonly type: "submitScore"; readonly leaderboardId: string; readonly score: number };

export interface GpgsDefs {
  readonly achievements: readonly GpgsAchievementDef[];
  readonly leaderboards: readonly GpgsLeaderboardDef[];
}

/**
 * 분석 이벤트 → GPGS 액션 목록(순수 함수, Phaser/Capacitor 무관 → 단위테스트 대상).
 * sink(GpgsAchievementSink)가 이 결과를 GpgsPort로 실행한다. 매핑은 전부 데이터(gpgsData) 주도.
 *
 *  - 업적: triggerEvent 일치 + (conditionParam!="-"이면 params[conditionParam] >= conditionMin) 시
 *    standard→unlock / incremental→increment(step). step<=0인 incremental은 액션 생성 안 함.
 *  - 리더보드: triggerEvent 일치 + params[scoreParam]가 유한수면 submitScore.
 *
 * @param event analyticsEvents snake_case 이벤트명.
 * @param params 이벤트 파라미터(숫자/문자). 조건·점수 추출에 사용.
 */
export function resolveGpgsActions(
  event: string,
  params: Record<string, string | number> | undefined,
  defs: GpgsDefs,
): GpgsAction[] {
  const actions: GpgsAction[] = [];

  for (const a of defs.achievements) {
    if (a.triggerEvent !== event) continue;
    if (a.conditionParam !== GPGS_NO_CONDITION) {
      const v = numberParam(params, a.conditionParam);
      if (v === null || v < a.conditionMin) continue;
    }
    if (a.type === "incremental") {
      if (a.step > 0) actions.push({ type: "increment", achievementId: a.achievementId, steps: a.step });
    } else {
      actions.push({ type: "unlock", achievementId: a.achievementId });
    }
  }

  for (const l of defs.leaderboards) {
    if (l.triggerEvent !== event) continue;
    const score = numberParam(params, l.scoreParam);
    if (score !== null) actions.push({ type: "submitScore", leaderboardId: l.leaderboardId, score });
  }

  return actions;
}

/** params[key]를 유한수로 추출(없거나 비수치면 null). */
function numberParam(
  params: Record<string, string | number> | undefined,
  key: string,
): number | null {
  if (!params || !(key in params)) return null;
  const n = Number(params[key]);
  return Number.isFinite(n) ? n : null;
}
